/**
 * Auth Store
 *
 * Manages authentication state including:
 * - User information
 * - Access and refresh tokens
 * - Authentication status
 *
 * @module @schoolidb/store/authStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@schoolidb/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Authentication status
 */
export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Token data
 */
export interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Auth store state
 */
export interface AuthState {
  /** Current user */
  user: User | null;
  /** Token data */
  tokens: TokenData | null;
  /** Authentication status */
  status: AuthStatus;
  /** Last authentication error */
  error: string | null;
}

/**
 * Auth store actions
 */
export interface AuthActions {
  /** Set user and tokens after successful login */
  setAuth: (user: User, tokens: TokenData) => void;
  /** Update user data */
  setUser: (user: User) => void;
  /** Update tokens */
  setTokens: (tokens: TokenData) => void;
  /** Set authentication status */
  setStatus: (status: AuthStatus) => void;
  /** Set error message */
  setError: (error: string | null) => void;
  /** Clear all auth data (logout) */
  clearAuth: () => void;
  /** Check if tokens are expired */
  isTokenExpired: () => boolean;
  /** Check if user is authenticated */
  isAuthenticated: () => boolean;
}

export type AuthStore = AuthState & AuthActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  tokens: null,
  status: 'idle',
  error: null,
};

// ============================================================================
// Store
// ============================================================================

/**
 * Auth store with persistence
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: (user, tokens) => {
        set({
          user,
          tokens,
          status: 'authenticated',
          error: null,
        });
      },

      setUser: (user) => {
        set({ user });
      },

      setTokens: (tokens) => {
        set({ tokens });
      },

      setStatus: (status) => {
        set({ status });
      },

      setError: (error) => {
        set({ error });
      },

      clearAuth: () => {
        set(initialState);
      },

      isTokenExpired: () => {
        const { tokens } = get();
        if (!tokens) return true;
        return Date.now() >= tokens.expiresAt;
      },

      isAuthenticated: () => {
        const { status, tokens } = get();
        return status === 'authenticated' && tokens !== null && !get().isTokenExpired();
      },
    }),
    {
      name: 'schoolidb-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        status: state.status,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectUser = (state: AuthStore) => state.user;
export const selectTokens = (state: AuthStore) => state.tokens;
export const selectAuthStatus = (state: AuthStore) => state.status;
export const selectAuthError = (state: AuthStore) => state.error;
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated();
